/**
 * ChartParser - Detects and extracts chart data from ChatGPT responses
 */

export class ChartParser {
  /**
   * Detect if response contains a chart and what type
   */
  static detect(chatResponse) {
    if (!chatResponse) return { hasChart: false };

    // Method 1: Explicit chart type marker
    const typeMatch = chatResponse.match(/\[CHART_TYPE:\s*(\w+-?\w*)\]/i);
    if (typeMatch) {
      return {
        type: typeMatch[1].toLowerCase(),
        hasChart: true,
      };
    }

    // Method 2: Detect from JSON structure
    if (chatResponse.includes('```json')) {
      const jsonData = this.extractJSON(chatResponse);
      if (jsonData && jsonData.type) {
        return {
          type: jsonData.type,
          hasChart: true,
        };
      }
    }

    // Method 3: Detect from content keywords
    const contentDetection = this.detectFromContent(chatResponse);
    return contentDetection;
  }

  /**
   * Extract JSON data from response
   */
  static extractJSON(chatResponse) {
    // Try to find JSON in code blocks (with or without language specifier)
    const jsonBlockMatch = chatResponse.match(/```(?:json)?\s*\n([\s\S]*?)\n```/);
    if (jsonBlockMatch) {
      try {
        const jsonStr = jsonBlockMatch[1].trim();
        return JSON.parse(jsonStr);
      } catch (e) {
        console.error('Failed to parse JSON block:', e);
      }
    }

    // Try to find raw JSON (look for complete objects)
    const jsonMatches = chatResponse.match(/\{(?:[^{}]|(?:\{[^{}]*\}))*\}/g);
    if (jsonMatches) {
      // Try each match, starting with the longest (most likely to be complete)
      const sortedMatches = jsonMatches.sort((a, b) => b.length - a.length);
      for (const match of sortedMatches) {
        try {
          const parsed = JSON.parse(match);
          // Check if it looks like chart data
          if (parsed.type || parsed.data || parsed.nodes || parsed.children) {
            return parsed;
          }
        } catch (e) {
          // Continue to next match
        }
      }
    }

    return null;
  }

  /**
   * Extract data based on chart type
   */
  static extractData(chatResponse, chartType) {
    const jsonData = this.extractJSON(chatResponse);
    
    if (!jsonData) return null;

    // For org-chart, if data is nested, extract it
    if (chartType === 'org-chart') {
      if (jsonData.data && jsonData.data.name) {
        return jsonData.data;
      }
      if (jsonData.name) {
        return jsonData;
      }
    }

    // For flowchart and network, return as is if it has the right structure
    if ((chartType === 'flowchart' || chartType === 'network') && jsonData.nodes) {
      return jsonData;
    }

    // For timeline, return as is if it has milestones
    if (chartType === 'timeline' && (jsonData.milestones || jsonData.items)) {
      return jsonData;
    }

    // Return the data property if it exists, otherwise return the whole object
    return jsonData.data || jsonData;
  }

  /**
   * Smart detection based on content analysis
   */
  static detectFromContent(content) {
    const lowerContent = content.toLowerCase();

    // Try to extract JSON first
    const jsonData = this.extractJSON(content);
    
    // Check if JSON has a type field
    if (jsonData && jsonData.type) {
      return { type: jsonData.type, hasChart: true };
    }

    // Check for performance dashboard structure
    if (jsonData && jsonData.dashboard) {
      return { type: 'performance-dashboard', hasChart: true };
    }

    // Check for table structure
    if (jsonData && (jsonData.headers || jsonData.rows || (jsonData.title && Array.isArray(jsonData.data)))) {
      return { type: 'table', hasChart: true };
    }

    // Check JSON structure for org chart
    if (jsonData && jsonData.data && jsonData.data.children) {
      return { type: 'org-chart', hasChart: true };
    }

    // Check JSON structure for timeline first (higher priority)
    if (jsonData && (jsonData.milestones || jsonData.items || jsonData.timeline)) {
      return { type: 'timeline', hasChart: true };
    }

    // Check JSON structure for flowchart
    if (jsonData && jsonData.nodes && jsonData.edges) {
      return { type: 'flowchart', hasChart: true };
    }

    // Performance dashboard detection from text
    if (lowerContent.includes('performance scorecard') || 
        lowerContent.includes('performance dashboard') ||
        lowerContent.includes('executive summary') ||
        (lowerContent.includes('performance metrics') && lowerContent.includes('risk assessment'))) {
      return { type: 'performance-dashboard', hasChart: true };
    }

    // Table detection from text
    if (lowerContent.includes('table') || 
        (lowerContent.includes('headers') && lowerContent.includes('rows')) ||
        lowerContent.includes('performance metrics') ||
        lowerContent.includes('risk matrix')) {
      return { type: 'table', hasChart: true };
    }

    // Timeline detection (check first before other chart types)
    if (lowerContent.includes('timeline') || lowerContent.includes('gantt') || 
        lowerContent.includes('milestones') || lowerContent.includes('roadmap') ||
        lowerContent.includes('project timeline') || lowerContent.includes('implementation timeline') ||
        (lowerContent.includes('month') && lowerContent.includes('phase')) ||
        (lowerContent.includes('week') && lowerContent.includes('phase')) ||
        lowerContent.includes('schedule') || lowerContent.includes('project plan')) {
      return { type: 'timeline', hasChart: true };
    }

    // Org chart detection from text
    if ((lowerContent.includes('children') || lowerContent.includes('hierarchy')) &&
        (lowerContent.includes('ceo') || lowerContent.includes('manager') || 
         lowerContent.includes('director') || lowerContent.includes('organization') ||
         lowerContent.includes('org chart') || lowerContent.includes('organizational'))) {
      return { type: 'org-chart', hasChart: true };
    }

    // Flowchart detection (more specific to avoid conflicts)
    if ((lowerContent.includes('nodes') && lowerContent.includes('edges') && !lowerContent.includes('timeline')) ||
        (lowerContent.includes('flowchart') || lowerContent.includes('flow chart')) ||
        (lowerContent.includes('process flow') && !lowerContent.includes('timeline'))) {
      return { type: 'flowchart', hasChart: true };
    }

    // Mermaid detection
    if (lowerContent.includes('```mermaid') ||
        lowerContent.includes('graph td') ||
        lowerContent.includes('graph lr')) {
      return { type: 'mermaid', hasChart: true };
    }

    // Network/Graph detection
    if ((lowerContent.includes('network') || lowerContent.includes('graph')) &&
        lowerContent.includes('nodes') && !lowerContent.includes('timeline')) {
      return { type: 'network', hasChart: true };
    }

    return { hasChart: false };
  }

  /**
   * Parse complete response
   */
  static parse(chatResponse) {
    if (!chatResponse) {
      return {
        hasChart: false,
        text: '',
      };
    }
    
    const detection = this.detect(chatResponse);
    
    if (!detection.hasChart) {
      return {
        hasChart: false,
        text: chatResponse,
      };
    }

    const data = this.extractData(chatResponse, detection.type);
    
    if (!data) {
      return {
        hasChart: false,
        text: chatResponse,
      };
    }
    
    // Extract text without the chart data
    let text = chatResponse
      .replace(/\[CHART_TYPE:[\s\S]*?\]/gi, '')
      .replace(/```(?:json)?\s*\n[\s\S]*?\n```/g, '')
      .replace(/```mermaid[\s\S]*?```/g, '')
      .trim();

    return {
      hasChart: true,
      type: detection.type,
      data: data,
      text: text || 'Here is the visualization:',
    };
  }
}

export default ChartParser;
